/**
 * Account Details Card Component
 * 
 * Read-only display of user account information including
 * club membership, subscriptions, and passes.
 * 
 * @module pages/auth/components
 */

import type { UserWithClubs, UserClub } from '~/services/user';
import { isPlaceholderEmail } from '@butter/library/shared';

export interface AccountDetailsCardProps {
  /** User data to display */
  user: UserWithClubs | null;
  /** Whether data is loading */
  isLoading: boolean;
}

/**
 * Loading skeleton for account details
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4" aria-label="Loading account details">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
    </div>
  );
}

/**
 * Club info section
 */
function ClubSection({ club }: { club: UserClub }) {
  return (
    <section 
      className="border-l-4 border-green-500 pl-4 py-2" 
      aria-labelledby="club-membership-heading"
    >
      <h4 
        id="club-membership-heading" 
        className="font-medium text-fg-primary mb-2"
      >
        Joining:
      </h4>
      <div className="space-y-1 text-sm">
        <p className="font-medium text-green-700">{club.name}</p>
        {club.address && (
          <p className="text-fg-quaternary text-xs">{club.address}</p>
        )}
      </div>
    </section>
  );
}

/**
 * Account Details Card
 * 
 * Displays user's account information in a card format:
 * - Club membership
 * - Associated subscriptions
 * - Active passes
 * 
 * Shows loading skeleton while data is being fetched.
 * 
 * @example
 * ```tsx
 * const { data: user, isLoading } = useUserQuery({ userId: '123' });
 * 
 * <AccountDetailsCard user={user} isLoading={isLoading} />
 * ```
 */
export function AccountDetailsCard({ 
  user, 
  isLoading 
}: AccountDetailsCardProps) {
  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 p-6"
      role="region" 
      aria-labelledby="account-details-heading"
      aria-live="polite"
    >
      <h3 
        id="account-details-heading" 
        className="text-lg font-semibold text-fg-primary mb-6"
      >
        Account Details
      </h3>

      {/* Loading State */}
      {isLoading && <LoadingSkeleton />}

      {/* Empty State */}
      {!isLoading && !user && (
        <p className="text-fg-quaternary text-sm">
          Account information will appear here once loaded.
        </p>
      )}

      {/* User Data */}
      {!isLoading && user && (
        <div className="space-y-6">
          {/* Email */}
          <div className="text-sm">
            <span className="text-fg-quaternary">Email: </span>
            {isPlaceholderEmail(user.email) ? (
              <span className="text-amber-600 font-medium">No email provided (required)</span>
            ) : (
              <span className="text-fg-primary font-medium">{user.email}</span>
            )}
          </div>

          {/* Club Membership */}
          {user.clubs && user.clubs.length > 0 && (
            <ClubSection club={user.clubs[0]} />
          )}

          {/* No club info */}
          {(!user.clubs || user.clubs.length === 0) && (
            <p className="text-fg-quaternary text-sm">
              No club membership found.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
